/* eslint-env browser */

/**
 * home-marble.js — the Approach stage: suminagashi marbling that untangles.
 *
 * A WebGL fragment shader draws a band of fine ink lines over a pale wash,
 * like ink pulled across water. The band runs at 68 degrees through the
 * right of the stage and always drifts downstream (down the page). One
 * value, `tangle`, sets how far the lines fold back on themselves (1) or
 * run side by side (0).
 *
 * The stage pins while the three statements pass. Each time scroll moves
 * to the next statement, the marbling moves with the copy: the ink surges
 * downstream for a moment and the lines settle one step straighter
 * (tangle 1, 0.5, 0). Scrolling back folds them again. The current
 * statement reads at full ink.
 *
 * Colours come from the page: ink = --accent, paper = --surface-page (used
 * to mix the pale wash), read off the canvas's computed color /
 * background-color. The canvas is transparent where there is no ink. CSS places the band
 * per breakpoint: --marble-x / --marble-y (0-1 of the canvas) is a point on
 * its centre line, --marble-unit the length the pattern is drawn against.
 *
 * Progressive enhancement: without WebGL the section never gets `is-live`,
 * so the statements read as a plain list. Reduced motion: one still frame
 * in the untangled state, no pin, all statements at ink. The draw loop
 * stops while the stage is off screen.
 */

(function (window, document) {
    'use strict';

    var section = document.querySelector('.home-untangle');
    var stage = section && section.querySelector('.home-untangle-stage');
    var canvas = section && section.querySelector('.home-marble');
    if (!stage || !canvas) return;

    var gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: true });
    if (!gl) return;

    // Band angle from horizontal (leaning "/") and half-width, in units.
    var BAND = { angle: 68, width: 0.13 };
    // Render below device resolution; the image is soft, and the shader
    // costs per pixel.
    var SCALE = 0.75;

    var FRAG = [
        'precision highp float;',
        'uniform float uUnit;',
        'uniform vec2 uCenter;',
        'uniform vec2 uDir;',
        'uniform float uWidth;',
        'uniform float uTime;',
        'uniform float uTangle;',
        'uniform vec3 uInk;',
        'uniform vec3 uPaper;',
        'vec2 hash(vec2 p){',
        '  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));',
        '  return -1.0 + 2.0 * fract(sin(p) * 43758.5453);',
        '}',
        'float noise(vec2 p){',
        '  vec2 i = floor(p), f = fract(p);',
        '  vec2 u = f * f * (3.0 - 2.0 * f);',
        '  return mix(mix(dot(hash(i), f), dot(hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),',
        '             mix(dot(hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)), dot(hash(i + vec2(1.0)), f - vec2(1.0)), u.x), u.y);',
        '}',
        'float fbm(vec2 p){',
        '  float s = 0.0, a = 0.5;',
        '  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);',
        '  for (int i = 0; i < 5; i++){ s += a * noise(p); p = m * p; a *= 0.5; }',
        '  return s;',
        '}',
        'void main(){',
        '  vec2 uv = (gl_FragCoord.xy - uCenter) / uUnit;',
        // p.x runs along the flow, p.y across it (positive = upper-left side).
        '  vec2 p = vec2(dot(uv, uDir), dot(uv, vec2(-uDir.y, uDir.x)));',
        '  float T = uTangle;',
        '  float t = uTime * 0.04;',
        // Anisotropic domain warp: features stretch along the flow. Adding t
        // moves every feature towards -p.x, which is downstream.
        '  vec2 s = vec2(p.x * 0.6 + t, p.y * 1.8);',
        '  vec2 q = vec2(fbm(s), fbm(s + vec2(5.2, 1.3)));',
        '  vec2 r = vec2(fbm(s + 2.4 * q + vec2(1.7 + t * 0.6, 9.2)), fbm(s + 2.4 * q + vec2(8.3, 2.8)));',
        '  float warp = mix(0.03, 0.30, T);',
        // Bend: the centre line curves like a river, so the band never reads
        // as a straight trunk. The bend moves far slower than the ink.
        '  float meander = 0.17 * sin(p.x * 2.0 + 0.6 + t * 0.4) + 0.12 * fbm(vec2(p.x * 0.7 + t, 4.0));',
        '  float yw = p.y + warp * r.y + meander;',
        // The width breathes along the flow.
        '  float w = uWidth * max(0.45, 1.0 + 1.4 * fbm(vec2(p.x * 1.1 + t * 1.2, 7.7)));',
        // Braid: a thinner stream peels off the lower-right side and rejoins.
        // Tangled = wide apart, untangled = merged into the main stream.
        '  float split = mix(0.02, 0.17, T) * (0.6 + 0.4 * sin(p.x * 2.3 + t * 2.5 + 2.0));',
        // Asymmetric: a defined edge on the upper-left, a slow fade into the
        // wash on the lower-right; the densest ink runs near the defined edge.
        '  float d = yw / (yw > 0.0 ? w * 0.7 : w * 1.5);',
        '  float band = max(exp(-d * d), 0.8 * exp(-pow((yw + split) / (w * 0.45), 2.0)));',
        '  float core = exp(-pow((yw - w * 0.3) / (w * 0.32), 2.0));',
        // Pale wash: wide on the lower-right side, a narrow halo upper-left.
        '  float side = smoothstep(-0.05, -0.25, yw) * smoothstep(-1.1, -0.4, yw) + 0.3 * smoothstep(0.02, 0.15, yw) * smoothstep(0.6, 0.25, yw);',
        // Fine ink lines: two families at different spacing, each pushed by
        // its own fine warp, so lines bunch, merge and split.
        '  float fw = fbm(vec2(p.x * 1.6 + t * 1.5, yw * 9.0) + 7.0 * q);',
        '  float l1 = pow(0.5 + 0.5 * sin(yw * 420.0 + 6.0 * fw), 10.0);',
        '  float l2 = pow(0.5 + 0.5 * sin(yw * 170.0 + 4.0 * fw + 1.3), 14.0);',
        '  float lines = max(l1 * smoothstep(0.0, 0.5, band), l2);',
        // Stray lines: thin lines peel away past the band's edge, so it
        // fades into the water instead of ending at a hard edge.
        '  float stray = l2 * 0.28 * exp(-pow(yw / (w * 2.6), 2.0)) * smoothstep(-0.1, 0.25, fw);',
        // Mottled wash and deep pools.
        '  float mott = fbm(vec2(p.x * 2.2 + t, yw * 6.0) + r * 2.0);',
        '  float pool = smoothstep(0.08, 0.32, mott) * band;',
        '  float washAmt = clamp(band * 0.75 + side * (0.15 + 0.85 * smoothstep(-0.15, 0.35, mott + 0.3 * r.x)), 0.0, 1.0);',
        '  float inkAmt = clamp(band * lines * 0.95 + core * 0.3 + pool * 0.5 + stray + side * l2 * 0.3 * smoothstep(0.05, 0.3, -mott), 0.0, 1.0);',
        // Transparent where there is no ink: wash, then ink, laid over
        // whatever is behind the canvas. Output is premultiplied alpha.
        '  vec3 wash = mix(uPaper, uInk, 0.28);',
        '  float wa = washAmt * (1.0 + 0.1 * noise(gl_FragCoord.xy * 0.9));',
        '  float ia = inkAmt * 0.9;',
        '  float a = clamp(1.0 - (1.0 - wa) * (1.0 - ia), 0.0, 1.0);',
        '  vec3 rgb = wash * wa * (1.0 - ia) + uInk * ia;',
        '  gl_FragColor = vec4(rgb, a);',
        '}'
    ].join('\n');

    function compile(type, src) {
        var sh = gl.createShader(type);
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        return gl.getShaderParameter(sh, gl.COMPILE_STATUS) ? sh : null;
    }

    var vs = compile(gl.VERTEX_SHADER, 'attribute vec2 a;void main(){gl_Position=vec4(a,0.0,1.0);}');
    var fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    // One triangle that covers the viewport.
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    var u = {};
    ['uUnit', 'uCenter', 'uDir', 'uWidth', 'uTime', 'uTangle', 'uInk', 'uPaper'].forEach(function (name) {
        u[name] = gl.getUniformLocation(prog, name);
    });

    // Any CSS colour (hex, rgb, oklch) to [r, g, b] in 0-1, via a 1px 2D canvas.
    function toRgb(css) {
        var c = document.createElement('canvas');
        c.width = c.height = 1;
        var ctx = c.getContext('2d');
        ctx.fillStyle = css;
        ctx.fillRect(0, 0, 1, 1);
        var d = ctx.getImageData(0, 0, 1, 1).data;
        return [d[0] / 255, d[1] / 255, d[2] / 255];
    }

    var rad = BAND.angle * Math.PI / 180;
    gl.uniform2f(u.uDir, Math.cos(rad), Math.sin(rad));
    gl.uniform1f(u.uWidth, BAND.width);

    var place = { x: 0.62, y: 0.5, unit: 816, crop: 0 };
    function readStyle() {
        var st = window.getComputedStyle(canvas);
        place.x = parseFloat(st.getPropertyValue('--marble-x')) || 0.62;
        place.y = parseFloat(st.getPropertyValue('--marble-y')) || 0.5;
        place.unit = parseFloat(st.getPropertyValue('--marble-unit')) || 816;
        place.crop = parseFloat(st.getPropertyValue('--marble-crop')) || 0;
        gl.uniform3fv(u.uInk, toRgb(st.color));
        gl.uniform3fv(u.uPaper, toRgb(st.backgroundColor));
    }

    // speed scales the drift clock; a step change raises it for a moment.
    var state = { p: 0, tangle: 1, time: 20, speed: 1 };
    var STEPS = [1, 0.5, 0];
    var step = -1;
    var surge = null;
    // Test hook: ?freeze holds the drift clock and applies each step's
    // tangle at once, so screenshots of the stage repeat exactly. Without
    // the parameter nothing changes.
    var FREEZE = /[?&]freeze(=|&|$)/.test(window.location.search);
    var items = section.querySelectorAll('.home-untangle-item');

    function draw() {
        var r = canvas.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var k = Math.min(window.devicePixelRatio || 1, 2) * SCALE;
        var w = Math.round(r.width * k);
        var h = Math.round(r.height * k);
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
            gl.viewport(0, 0, w, h);
        }
        // The mask hides the canvas left of --marble-crop, so don't shade
        // those pixels. Drawn pixels keep their coordinates; output is
        // unchanged.
        var x0 = Math.floor(place.crop * w);
        if (x0 > 0) {
            gl.enable(gl.SCISSOR_TEST);
            gl.scissor(x0, 0, w - x0, h);
        } else {
            gl.disable(gl.SCISSOR_TEST);
        }
        gl.uniform1f(u.uUnit, place.unit * k);
        // GL's y axis points up; place.y is measured from the top.
        gl.uniform2f(u.uCenter, place.x * w, (1 - place.y) * h);
        gl.uniform1f(u.uTime, state.time);
        gl.uniform1f(u.uTangle, state.tangle);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function setCurrent(index) {
        for (var i = 0; i < items.length; i++) {
            items[i].classList.toggle('is-current', index < 0 || i === index);
        }
    }

    // Scroll progress (0-1) to step. On a step change the copy switches
    // (CSS opacity, 320ms) and the marbling transitions with it: a surge of
    // drift, then the tangle eases to the step's value.
    function render() {
        var next = state.p < 0.34 ? 0 : state.p < 0.67 ? 1 : 2;
        if (next === step) return;
        var first = step < 0;
        step = next;
        setCurrent(step);
        if (first || FREEZE) {
            state.tangle = STEPS[step];
            return;
        }
        gsap.to(state, { tangle: STEPS[step], duration: 1.6, ease: 'power3.inOut', overwrite: 'auto' });
        if (surge) surge.kill();
        surge = gsap.timeline()
            .to(state, { speed: 9, duration: 0.35, ease: 'power2.out' })
            .to(state, { speed: 1, duration: 1.6, ease: 'power2.inOut' });
    }

    section.classList.add('is-live');
    readStyle();

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

    if (reduce.matches || !hasGsap) {
        state.tangle = 0;
        setCurrent(-1);
        draw();
        window.addEventListener('resize', function () {
            readStyle();
            draw();
        });
        return;
    }

    window.addEventListener('resize', readStyle);

    // Draw loop: runs only while the stage is on screen. The drift runs on
    // its own clock; scroll only changes the tangle.
    var raf = 0;
    var last = 0;
    function frame(ms) {
        if (!FREEZE) state.time += state.speed * Math.min(ms - (last || ms), 100) / 1000;
        last = ms;
        draw();
        raf = window.requestAnimationFrame(frame);
    }
    function setRunning(on) {
        window.cancelAnimationFrame(raf);
        if (on) {
            last = 0;
            raf = window.requestAnimationFrame(frame);
        }
    }
    if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
            setRunning(entries[0].isIntersecting);
        }).observe(stage);
    } else {
        setRunning(true);
    }

    gsap.registerPlugin(ScrollTrigger);

    // Ease the raw scroll value into state.p so fast wheels still read as a
    // continuous untangling.
    function follow(self) {
        if (FREEZE) {
            state.p = self.progress;
            render();
            return;
        }
        gsap.to(state, { p: self.progress, duration: 0.45, ease: 'power2.out', overwrite: 'auto', onUpdate: render });
    }

    var mm = gsap.matchMedia();

    mm.add('(min-width: 641px)', function () {
        ScrollTrigger.create({
            trigger: section,
            start: 'center center',
            end: '+=100%',
            pin: stage,
            onUpdate: follow,
            onRefresh: render
        });
    });

    // Phones: no pin; the art is a sticky band (CSS) and the section's own
    // scroll drives the untangling.
    mm.add('(max-width: 640px)', function () {
        ScrollTrigger.create({
            trigger: section,
            start: 'top top',
            end: 'bottom 70%',
            onUpdate: follow,
            onRefresh: render
        });
    });

    render();
    draw();

})(window, document);
